import { createSelector, lruMemoize } from '@reduxjs/toolkit';
import type { ModelEvent } from './model-event';
import type { State } from '../../types';
import { LocalDate } from '../../../utilities/local-date';
import { findClosestInstance } from './find-closest-instance';
import { sortBy } from 'lodash';
import { dateTimeDescription } from '../../utilities';
import { eventAssigneesText } from '../../eventAssigneesText';
import { events } from './events';
import { tasksNotDeletedAndNotLocked } from '../responsibilities/tasks-not-deleted-and-not-locked';
import { functionsNotDeleted } from '../responsibilities/functions-not-deleted';
import { employeesShortNamesAndI } from '../employees/employees-short-names-and-i';
import { getContactsAsUsers } from '../partners/get-contacts-as-users';
import { eventOccurrenceByTaskUuid } from './event-occurrence-by-task-uuid';

const modelEventsSelector = createSelector(
  [
    events,
    tasksNotDeletedAndNotLocked,
    functionsNotDeleted,
    employeesShortNamesAndI,
    getContactsAsUsers,
    eventOccurrenceByTaskUuid,
    (_state: State, relatedAssetForNew: string) => relatedAssetForNew,
  ],
  (eventModels, tasks, functions, employees, contacts, eventByTask, relatedAssetForNew): ModelEvent[] => {
    const now = LocalDate.now();
    const result: ModelEvent[] = eventModels.map((e) => {
      const o = findClosestInstance(e.eventOccurrences, now);
      const assigneesText = eventAssigneesText(o, functions, employees, contacts);
      return {
        name: o.name ?? '',
        durationMinutes: o.durationMinutes,
        assignedToFunction: o.functionUuid ?? '',
        assignedToEmployees: o.employees ?? [],
        assignedToContacts: o.contacts ?? [],
        notes: o.notes,
        time: o.time || 'NONE',
        procedures: o.description ?? '',
        taskUuid: o.taskUuid ?? '',
        futureNotDoneOccurrences: sortBy(
          e.eventOccurrences
            .filter((occ) => {
              return !occ.doneDate && (occ.date === '' || LocalDate.fromString(occ.date).isSameOrAfter(now));
            })
            .map((occ) => {
              const assetRelated =
                relatedAssetForNew !== '' &&
                occ.relatedAssets?.some((a) => a.assetUuid === relatedAssetForNew) === true;
              return {
                occurrenceUuid: occ.uuid,
                href: '/eventOccurrences/' + occ.uuid,
                dateTime: (occ.date ?? '') + (occ.time ?? ''),
                durationMinutes: occ.durationMinutes,
                classified: false,
                restricted: false,
                displayDate: dateTimeDescription(occ.date ?? '', occ.time ?? ''),
                assignees: assigneesText,
                assetRelated,
              };
            }),
          (x) => x.dateTime,
          (x) => x.occurrenceUuid,
        ),
      };
    });

    const taskModels: ModelEvent[] = tasks
      .filter((t) => !eventByTask.has(t.uuid))
      .map((t) => {
        return {
          name: t.name ?? '',
          time: 'NONE',
          durationMinutes: 0,
          assignedToFunction: '',
          assignedToEmployees: [],
          assignedToContacts: [],
          notes: '',
          procedures: t.procedures ?? '',
          taskUuid: t.uuid,
          futureNotDoneOccurrences: [],
        };
      });

    return sortBy([...result, ...taskModels], ['name']);
  },
  { memoize: lruMemoize, memoizeOptions: { maxSize: 4 } },
);

export function getModelEvents(state: State, relatedAssetForNew: string): ModelEvent[] {
  return modelEventsSelector(state, relatedAssetForNew);
}
